import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../../../hooks/useUser";
import {
  createUser,
  UserAdministrationError,
} from "../../../lib/userAdministration";

interface MentorFormData {
  email: string;
  name: string;
  specialization: string;
}

export function AddMentor() {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<MentorFormData>({
    email: "",
    name: "",
    specialization: "",
  });

  useEffect(() => {
    if (!user) return;

    if (!user.isAdmin) {
      setError("You must be an admin to create mentors");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.isAdmin) {
      setError("You must be an admin to create mentors");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createUser({
        email: formData.email,
        name: formData.name,
        specialization: formData.specialization,
        role: "MENTOR",
      });

      // Success! Navigate back to mentor list
      navigate("/coordinator/mentors");
    } catch (err) {
      console.error("Error in mentor creation:", err);
      if (err instanceof UserAdministrationError) {
        setError(`Failed to create mentor (${err.step}): ${err.message}`);
      } else {
        setError("An unexpected error occurred while creating the mentor");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">Add New Mentor</h1>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-6 rounded-lg bg-white p-6 shadow-sm"
      >
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Full Name
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!user?.isAdmin}
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!user?.isAdmin}
          />
        </div>

        <div>
          <label
            htmlFor="specialization"
            className="block text-sm font-medium text-gray-700"
          >
            Specialization
          </label>
          <input
            type="text"
            id="specialization"
            value={formData.specialization}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                specialization: e.target.value,
              }))
            }
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#4CAF50] focus:outline-none focus:ring-[#4CAF50]"
            required
            disabled={!user?.isAdmin}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !user?.isAdmin}
          className="flex w-full justify-center rounded-md border border-transparent bg-[#4CAF50] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? "Creating Mentor..." : "Create Mentor"}
        </button>
      </form>
    </div>
  );
}
